import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Image as ImageIcon, MapPin } from 'lucide-react';
import type { Currency, Locale, MiniSiteGallery, MiniSiteImage } from '@worki/shared';
import { cn } from '../lib/cn.js';
import { swipeStep } from '../lib/swipe.js';
import { formatRelative, money } from '../lib/format.js';

/**
 * MS-007 — one album card, for both the screen that edits it and the site a
 * stranger reads.
 *
 * The two used to be written out twice: the owner's card in Tailwind over
 * `glass-card`, the visitor's in a separate `.ms-card` stylesheet. Same album,
 * same fields, two drawings — so "view as a site" showed something the owner
 * had never seen. Concretely, with the same payload in front of both, the
 * owner's card carried the sale and new badges and flipped through the album
 * under two arrows, and the visitor's card carried none of it: an album marked
 * on sale was on sale only on the screen where nobody buys.
 *
 * So the drawing lives here once and the hosts pass in what is theirs: the
 * editor's tick and ⋮ menu, the site's "get details". Anything either side adds
 * to the card from now on is added to both, which is the only way the two stay
 * the same design.
 */

/** How long an album wears the "new" badge. */
export const NEW_FOR_DAYS = 14;
export const isNewGallery = (iso: string) => Date.now() - new Date(iso).getTime() < NEW_FOR_DAYS * 86_400_000;

/** The image the card shows: the one flipped to, else the chosen cover, else the first. */
export const galleryCover = (gallery: MiniSiteGallery, index: number): MiniSiteImage | undefined =>
  gallery.images[index] ?? gallery.images.find((image) => image.id === gallery.coverImageId) ?? gallery.images[0];

export function MiniSiteGalleryCard({
  gallery,
  currency,
  locale,
  rtl,
  layout = 'grid',
  selected = false,
  flip = true,
  onOpen,
  coverProps,
  coverOverlay,
  titleAction,
  foot,
  className,
  liked = false,
  onLike,
}: {
  gallery: MiniSiteGallery;
  currency: Currency;
  locale: Locale;
  rtl: boolean;
  layout?: 'grid' | 'list';
  selected?: boolean;
  /** Whether the album can be flipped through on the card itself. */
  flip?: boolean;
  onOpen: () => void;
  /** The editor's long-press handlers, which the site has no use for. */
  coverProps?: React.HTMLAttributes<HTMLDivElement>;
  /** Drawn over the cover — the editor's selection tick. */
  coverOverlay?: ReactNode;
  /** Beside the album name — the editor's ⋮ menu. */
  titleAction?: ReactNode;
  /** Opposite the price — the editor's "updated" stamp, the site's call to action. */
  foot?: ReactNode;
  className?: string;
  /** Whether this visitor has tapped the heart. */
  liked?: boolean;
  /** The site's heart. Without it the count is shown and nothing can be pressed. */
  onLike?: () => void;
}) {
  const { t } = useTranslation();
  /*
   * The slide lives on the card rather than in either host, because a page that
   * keeps it is a page that can forget to: the site never had one, which is why
   * a visitor could not see past the cover of an album that had six photos in it.
   */
  const [at, setAt] = useState(0);
  /* Where the finger went down on the cover, and whether it left as a swipe. */
  const touch = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  const image = galleryCover(gallery, at);
  const step = (direction: number) => {
    const count = gallery.images.length;
    if (count > 0) setAt((current) => (current + direction + count) % count);
  };
  const price =
    gallery.price === null
      ? null
      : money(gallery.price, currency, locale);

  return (
    <article
      className={cn(
        'glass-card rounded-xl overflow-hidden transition-all',
        layout === 'list' && 'flex items-stretch gap-3',
        selected && 'ring-2 ring-primary',
        className,
      )}
    >
      <div
        role="button"
        tabIndex={0}
        {...coverProps}
        onTouchStart={(event) => {
          const finger = event.touches[0];
          touch.current = finger ? { x: finger.clientX, y: finger.clientY } : null;
        }}
        onTouchEnd={(event) => {
          const from = touch.current;
          const finger = event.changedTouches[0];
          touch.current = null;
          if (!flip || !from || !finger || gallery.images.length < 2) return;
          const direction = swipeStep(finger.clientX - from.x, finger.clientY - from.y, rtl);
          if (!direction) return;
          // The cover opens the album when tapped, and a swipe ends in a tap as
          // far as the browser is concerned. Flipping past a picture must not
          // open the album it belongs to.
          swiped.current = true;
          step(direction);
        }}
        onClick={() => {
          if (swiped.current) {
            swiped.current = false;
            return;
          }
          onOpen();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen();
          }
        }}
        className={cn('relative bg-muted cursor-pointer', layout === 'grid' ? 'aspect-[4/3]' : 'w-28 shrink-0')}
      >
        {image ? (
          <img src={image.url} alt={image.altText ?? gallery.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <ImageIcon className="h-6 w-6" />
          </span>
        )}
        {coverOverlay}
        <span className="absolute top-2 end-2 flex items-center gap-1">
          {gallery.onSale && (
            <span className="px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground text-[9px] font-bold tracking-widest">
              {t('miniSite.badgeSale')}
            </span>
          )}
          {isNewGallery(gallery.createdAt) && (
            <span className="px-1.5 py-0.5 rounded bg-success text-success-foreground text-[9px] font-bold">
              {t('miniSite.badgeNew')}
            </span>
          )}
          {!gallery.published && (
            <span className="px-1.5 py-0.5 rounded bg-foreground/70 text-background text-[9px] font-bold">
              {t('miniSite.hidden')}
            </span>
          )}
        </span>
        {/* UI-7.2 — flipping through an album without opening it is a swipe
            across the cover now. The two arrows that did it sat on a cover 112
            pixels wide in the list layout, where they covered most of the
            picture they were there to show. The dots are what is left: they say
            there is more than one picture, which is the part the arrows were
            also doing. */}
        {flip && gallery.images.length > 1 && (
          <span className="absolute bottom-1 inset-x-0 flex items-center justify-center gap-1">
            {gallery.images.map((picture, index) => (
              <span
                key={picture.id}
                className={cn('h-1 rounded-full', index === at ? 'w-2.5 bg-card' : 'w-1 bg-card/60')}
              />
            ))}
          </span>
        )}
      </div>

      <div className="p-3 flex-1 min-w-0 space-y-1">
        <div className="flex items-start gap-1">
          <h3 className="text-sm font-semibold truncate flex-1">{gallery.name}</h3>
          {titleAction}
        </div>
        {gallery.description && <p className="text-xs text-muted-foreground line-clamp-2">{gallery.description}</p>}
        {/*
          How many pictures, how many people have looked, and how many are left.
          The stock count is the owner's own answer to "is this still
          available" and never reached the visitor at all; zero views is left
          off, because it reads as a warning rather than as information.
        */}
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span className="ltr-nums">{t('miniSite.imagesCount', { count: gallery.images.length })}</span>
          {gallery.viewCount > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="ltr-nums">{t('miniSite.viewsCount', { count: gallery.viewCount })}</span>
            </>
          )}
          {gallery.city && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="h-3 w-3" />
                {gallery.city}
              </span>
            </>
          )}
          {gallery.availableQuantity !== null && (
            <>
              <span aria-hidden>·</span>
              <span className="ltr-nums">
                {t('miniSite.quantity')}: {gallery.availableQuantity}
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          {price ? (
            <span className="text-sm font-bold text-primary">
              {gallery.priceFrom && `${t('miniSite.public.priceFrom')} `}
              <span className="ltr-nums">{price}</span>
            </span>
          ) : (
            <span />
          )}
          {onLike ? (
            <button
              type="button"
              aria-label={t('miniSite.like')}
              aria-pressed={liked}
              onClick={onLike}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground ltr-nums"
            >
              <Heart className={cn('h-4 w-4', liked && 'text-destructive')} fill={liked ? 'currentColor' : 'none'} />
              {gallery.likeCount}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground ltr-nums" aria-label={t('miniSite.like')}>
              <Heart className="h-4 w-4" />
              {gallery.likeCount}
            </span>
          )}
          {foot}
        </div>
      </div>
    </article>
  );
}

/** The editor's own stamp under a card, kept here so both sides read alike. */
export function GalleryUpdatedStamp({ iso, locale }: { iso: string; locale: Locale }) {
  return <span className="text-[10px] text-muted-foreground">{formatRelative(iso, locale)}</span>;
}
